// src/API/apiService.ts
import apiClient from './apiClient';
import { AxiosResponse } from 'axios';

// baseURL は環境変数から取得し、末尾のスラッシュを削除
const baseURL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
// baseURL に "/api" が含まれていなければ、付与する
const apiPrefix = baseURL.includes('/api') ? '' : '/api';

// CSRF 用の URL では、もし baseURL が "/api" で終わっている場合はそれを取り除く
const csrfBaseURL = baseURL.endsWith('/api') ? baseURL.slice(0, -4) : baseURL;

/* 既存の型定義とAPIエンドポイント */

export interface Location {
  latitude: number;
  longitude: number;
}

export interface SubArea {
  id: number;
  name: string;
}

export interface PropertiesResponse {
  data: Property[];
  meta: {
    total: number;
  };
}

export interface Property {
  property_id: number;
  region_id: number;
  region: string;
  area_id: number;
  area: string;
  sub_areas: SubArea[];
  name: string;
  is_public: number;
  sort_number: number;
  chillnn_id: string;
}

export const getPropertiesAll = (): Promise<AxiosResponse> => {
  return apiClient.get(`${baseURL}${apiPrefix}/v1/properties/locations`);
};

export const getPropertieslist = (): Promise<AxiosResponse<any>> => {
  return apiClient.get<any>(`${baseURL}${apiPrefix}/v1/properties/list`);
};

export const getRoomTypeIndex = (property_id: any): Promise<any> => {
  return apiClient.get(
    `${baseURL}${apiPrefix}/v1/properties/${property_id}/room-types/aggregations`,
    { withCredentials: true }
  );
};

export const getRoomTypeDetail = (
  property_id: any,
  roomType_id: any
): Promise<any> => {
  return apiClient.get(
    `${baseURL}${apiPrefix}/v1/properties/${property_id}/room-types/${roomType_id}/aggregations`
  );
};

export const getPropertyAccess = (property_id: any): Promise<any> => {
  return apiClient.get<PropertiesResponse>(
    `${baseURL}${apiPrefix}/v1/properties/${property_id}/access`
  );
};

export const getNeighborhoodById = (neighborhood_id: any): Promise<any> => {
  return apiClient.get<PropertiesResponse>(
    `${baseURL}${apiPrefix}/v1/neighborhoods/${neighborhood_id}`
  );
};

export const getNeighborhoodsByProperty = (
  property_id: number | string
): Promise<AxiosResponse<any>> => {
  return apiClient.get(
    `${baseURL}${apiPrefix}/v1/properties/${property_id}/neighborhoods`
  );
};

export const getNeighborhoodList = (): Promise<any> => {
  return apiClient.get<PropertiesResponse>(
    `${baseURL}${apiPrefix}/v1/neighborhoods/list`
  );
};

export const getNewsList = (): Promise<any> => {
  return apiClient.get<PropertiesResponse>(`${baseURL}${apiPrefix}/v1/news`);
};

export const getAreasAndNeighborhoodTags = (): Promise<AxiosResponse<any>> => {
  return apiClient.get(`${baseURL}${apiPrefix}/v1/areas-neighborhood-tags`);
};

/* ここから認証関連の関数 */

// ドキュメントのクッキーから "XSRF-TOKEN" を取得する関数
export const getXsrfToken = (): string | null => {
  const match = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * CSRFトークンを取得するために /sanctum/csrf-cookie エンドポイントを呼び出す
 */
export const getSanctumCsrfCookie = async (): Promise<string | null> => {
  try {
    console.log('Fetching CSRF cookie...');
    const response = await apiClient.get(`${csrfBaseURL}/sanctum/csrf-cookie`, {
      withCredentials: true,
    });
    console.log('CSRF cookie set:', response.data);
    const xsrfToken = getXsrfToken();
    console.log(
      xsrfToken ? `decoded XSRF-TOKEN: ${xsrfToken}` : 'XSRF-TOKEN not found'
    );
    return xsrfToken;
  } catch (error: any) {
    console.error('Error fetching CSRF cookie:', error);
    throw error;
  }
};

/**
 * /v1/admin/login エンドポイントを呼び出してログインを実施する関数
 */
export const postApiV1AdminLogin = async (params: {
  email: string;
  password: string;
}): Promise<AxiosResponse<any>> => {
  try {
    const xsrfToken = getXsrfToken();
    const response = await apiClient.post(
      `${baseURL}${apiPrefix}/v1/admin/login`,
      params,
      {
        withCredentials: true,
        headers: {
          'X-XSRF-TOKEN': xsrfToken || '',
        },
      }
    );
    return response;
  } catch (error: any) {
    console.error('Error in login API:', error);
    throw error;
  }
};

/* ここから各画面で利用するエンドポイント */

// 管理画面用：部屋情報保存（新規作成用）
export const postAdminRoomSave = async (
  data: any
): Promise<AxiosResponse<any>> => {
  return apiClient.post(`${baseURL}${apiPrefix}/v1/rooms`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// 問い合わせ送信用：contact/general/submit
export const postContactGeneralSubmit = async (
  payload: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/contact/general/submit`,
    payload,
    {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};

// 契約申込送信用：contracts（multipart/form-data）
export const postContractSubmission = async (
  formData: FormData
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(`${baseURL}${apiPrefix}/v1/contracts`, formData, {
    withCredentials: true,
    headers: {
      'X-XSRF-TOKEN': xsrfToken || '',
    },
  });
};

/* ===================== 管理者用エンドポイント ===================== */

// 1. 認証・ログアウト関連
export const postAdminLogout = async (): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/logout`,
    {},
    {
      withCredentials: true,
      headers: {
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};

export const getAdminAuthCheck = (): Promise<AxiosResponse<any>> => {
  return apiClient.get(`${baseURL}${apiPrefix}/v1/admin/auth/check`, {
    withCredentials: true,
  });
};

// 2. 物件管理（編集用）
// 【追加】物件一覧取得（GET /v1/admin/properties）
export const getAdminProperties = (): Promise<AxiosResponse<any>> => {
  return apiClient.get(`${baseURL}${apiPrefix}/v1/admin/properties`, {
    withCredentials: true,
  });
};

export const getAdminPropertyDetail = (
  id: number | string
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.get(`${baseURL}${apiPrefix}/v1/admin/properties/${id}`, {
    withCredentials: true,
    headers: {
      'X-XSRF-TOKEN': xsrfToken || '',
    },
  });
};

// 【追加】物件新規作成（POST /v1/admin/properties）
export const postAdminProperty = (data: any): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(`${baseURL}${apiPrefix}/v1/admin/properties`, data, {
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': xsrfToken || '',
    },
  });
};

export const patchAdminProperty = (
  id: number | string,
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/properties/${id}`,
    data,
    {
      withCredentials: true,
      headers: {
        'X-XSRF-TOKEN': xsrfToken || '',
        'X-HTTP-Method-Override': 'PATCH',
      },
    }
  );
};

// 3. ルームタイプ管理（編集用）
// 【追加】ルームタイプステータス取得（GET /v1/admin/properties/{propertyId}/room-types/statuses）
export const getAdminRoomTypeStatuses = (
  propertyId: number | string
): Promise<AxiosResponse<any>> => {
  return apiClient.get(
    `${baseURL}${apiPrefix}/v1/admin/properties/${propertyId}/room-types/statuses`,
    { withCredentials: true }
  );
};

export const getAdminRoomTypeDetail = (
  propertyId: number | string,
  roomTypeId: number | string
): Promise<AxiosResponse<any>> => {
  return apiClient.get(
    `${baseURL}${apiPrefix}/v1/admin/properties/${propertyId}/room-types/${roomTypeId}`,
    { withCredentials: true }
  );
};

// 【追加】ルームタイプ新規作成（POST /v1/admin/properties/{propertyId}/room-types）
export const postAdminRoomType = (
  propertyId: number | string,
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/properties/${propertyId}/room-types`,
    data,
    {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};

export const patchAdminRoomType = (
  propertyId: number | string,
  roomTypeId: number | string,
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/properties/${propertyId}/room-types/${roomTypeId}`,
    data,
    {
      withCredentials: true,
      headers: {
        // 'Content-Type': 'application/json',
        'X-XSRF-TOKEN': xsrfToken || '',
        'X-HTTP-Method-Override': 'PATCH',
      },
    }
  );
};

// 4. 契約管理
export const getAdminContracts = (): Promise<AxiosResponse<any>> => {
  return apiClient.get(`${baseURL}${apiPrefix}/v1/admin/contracts`, {
    withCredentials: true,
  });
};

export const postAdminContractSubmit = async (
  id: number | string,
  formData: FormData
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    // URLをバックエンドのルーティングに合わせる
    `${baseURL}/v1/admin/contracts/${id}/submit`,
    formData,
    {
      withCredentials: true,
      headers: {
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};

export const postAdminContractConfirm = (
  id: number | string
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/contracts/${id}/confirm`,
    {},
    { withCredentials: true, headers: { 'X-XSRF-TOKEN': xsrfToken || '' } }
  );
};

export const postAdminContractCancel = (
  id: number | string
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/contracts/${id}/cancel`,
    {},
    { withCredentials: true, headers: { 'X-XSRF-TOKEN': xsrfToken || '' } }
  );
};

// 5. ネイバーフッド管理
export const getAdminNeighborhoods = (): Promise<AxiosResponse<any>> => {
  return apiClient.get(`${baseURL}${apiPrefix}/v1/admin/neighborhoods`, {
    withCredentials: true,
  });
};

export const getAdminNeighborhoodDetail = (
  id: number | string
): Promise<AxiosResponse<any>> => {
  return apiClient.get(`${baseURL}${apiPrefix}/v1/admin/neighborhoods/${id}`, {
    withCredentials: true,
  });
};

export const postAdminNeighborhood = (
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(`${baseURL}${apiPrefix}/v1/admin/neighborhoods`, data, {
    withCredentials: true,
    headers: {
      'X-XSRF-TOKEN': xsrfToken || '',
    },
  });
};

// applicant(運営に送付)
// BEルート: Route::post('/applicants/{id}/images', [ApplicantManagementController::class, 'resendIdentifyingImages']);
export const postApplicantResendIdentifyingImages = (
  id: number | string,
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/applicants/${id}/images`,
    data,
    {
      withCredentials: true,
      headers: {
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};

// contract history(申し込み変更)
// BEルート: Route::post('/contracts/{id}/change', [ContractHistoryManagementController::class, 'change']);
export const postAdminContractChange = (
  id: number | string,
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/contracts/${id}/change`,
    data,
    {
      withCredentials: true,
      headers: {
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};

//編集
export const patchAdminNeighborhood = (
  id: number | string,
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/neighborhoods/${id}`,
    data,
    {
      withCredentials: true,
      headers: {
        // 'Content-Type': 'application/json',
        'X-XSRF-TOKEN': xsrfToken || '',
        'X-HTTP-Method-Override': 'PATCH',
      },
    }
  );
};

export const deleteAdminNeighborhood = (
  id: number | string
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.delete(
    `${baseURL}${apiPrefix}/v1/admin/neighborhoods/${id}`,
    {
      withCredentials: true,
      data: { _method: 'DELETE' }, // ここで必要なデータを送信（例えば認証用のパラメータがあれば追加）
      headers: {
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};

// 6. 応募者管理
export const postAdminApplicantImages = (
  id: number | string,
  data: any
): Promise<AxiosResponse<any>> => {
  const xsrfToken = getXsrfToken();
  return apiClient.post(
    `${baseURL}${apiPrefix}/v1/admin/applicants/${id}/images`,
    data,
    {
      withCredentials: true,
      headers: {
        'X-XSRF-TOKEN': xsrfToken || '',
      },
    }
  );
};
